import { Trans } from '@lingui/react';
import { GenericError } from '@startuptools/ui/Error';
import { Alert } from '@startuptools/ui/Alert';
import { Button } from '@startuptools/ui/Button';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Radio } from '@startuptools/ui/Radio';
import { RadioGroup } from '@startuptools/ui/RadioGroup';
import { Typography } from '@startuptools/ui/Typography';
import { Stack } from '@startuptools/ui/Stack';
import { Box } from '@startuptools/ui/Box';
import { Skeleton } from '@startuptools/ui/Skeleton';
import { DateTime } from 'luxon';
import {
  type GqlCompany,
  type GqlCompleteShareCapitalChange,
  type GqlShareTransactionGroup,
  type GqlSnrShareCapitalChange,
  GqlTransactionKind,
} from '../../../../graphql/base-types.graphql';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { DateInput } from '@startuptools/ui/DateInput';
import { type ApolloError } from '@apollo/client';
import { nameCapitalize } from '../../../../pipes/name-capitalize.pipe';
import { useDefaultDateForTransactionGroup } from '../utils/default-date';
import { isNil } from 'lodash-es';
import { TransactionGroupFormSameDaySts } from './TransactionGroupFormSameDayGroups';
import { TransactionGroupFormSplit } from './TransactionGroupFormSplit';
import { useDateRange } from '../utils/stg-date-range';
import { Suspense, useEffect } from 'react';

export type StgFormData = {
  date: DateTime;
  sharesBefore: string;
  sharesAfter: string;
  beforeStgId: string;
  kind: GqlTransactionKind | 'next';
  roundUpShareholderId?: string;
};

const ErrorMsg = ({ error }: { error: ApolloError }) => {
  switch (error.message) {
    case 'INVALID_SPLIT_FACTOR':
      return (
        <Alert
          color="danger"
          endDecorator={
            <Button
              href="https://startuptools.org/se/aktiesplittar-som-inte-gar-att-lagga-in-i-aktieboken/"
              color="danger"
              target="_blank"
            >
              <Trans id="Read more" />
            </Button>
          }
        >
          <Trans id="Contact support to add this Split to the Share Register." />
        </Alert>
      );

    case 'MISSING_SPLIT_FACTOR':
      return (
        <Alert color="danger">
          <Trans id="Make sure to define the shares before and the shares after." />
        </Alert>
      );

    case 'SPLIT_FACTOR_ZERO':
      return (
        <Alert color="danger">
          <Trans id="Shares before and after can't be the same value." />
        </Alert>
      );

    default:
      return <GenericError />;
  }
};

export const CreateShareTransactionGroupForm = ({
  next,
  shareTransactionGroups,
  company,
  snrShareCapitalChanges,
  close,
  save,
  loading,
  error,
  prev,
  afterNext,
}: {
  defaultDate?: DateTime;
  prev?: GqlCompleteShareCapitalChange | null | undefined;
  next?: GqlCompleteShareCapitalChange | null | undefined;
  afterNext?: GqlCompleteShareCapitalChange | null | undefined;
  shareTransactionGroups: (Pick<
    GqlShareTransactionGroup,
    'transactionDate' | 'sortOrder' | 'kind' | 'snrShareCapitalChangeId' | 'id'
  > & { snrShareCapitalChange?: Pick<GqlSnrShareCapitalChange, 'name'> | null })[];
  company: Pick<GqlCompany, 'foundationDate' | 'id' | 'name'>;
  snrShareCapitalChanges: Pick<
    GqlSnrShareCapitalChange,
    'id' | 'name' | 'filingNumber' | 'serialNumber' | 'filingYear' | 'registrationDate' | 'decisionDate'
  >[];
  close: () => void;
  save: (data: StgFormData) => Promise<void>;
  loading: boolean;
  error: ApolloError | undefined;
}) => {
  const isFoundation = next?.name.startsWith('NYBILDNING');

  const { handleSubmit, control, setValue, formState } = useForm<StgFormData>({
    defaultValues: {
      date: DateTime.now().startOf('day'),
      sharesAfter: '0',
      sharesBefore: '0',
      kind: next ? 'next' : GqlTransactionKind.Issue,
      beforeStgId: '',
    },
  });

  const selectedKind = useWatch({ name: 'kind', control, exact: true });

  const currentKind = selectedKind === 'next' ? next?.kind : selectedKind;
  if (isNil(currentKind)) {
    throw new Error('missing currentKind');
  }
  const isAddingSplit = [GqlTransactionKind.Split, GqlTransactionKind.Merge].includes(currentKind);

  const { minDate, maxDate } = useDateRange(
    control,
    shareTransactionGroups,
    prev,
    isAddingSplit ? afterNext : next,
    company,
  );

  const defaultDate = useDefaultDateForTransactionGroup(
    currentKind,
    isAddingSplit,
    prev,
    isAddingSplit ? afterNext : next,
    minDate,
    shareTransactionGroups,
    company,
  );

  useEffect(() => {
    if (defaultDate) {
      setValue('date', defaultDate);
    }
  }, [defaultDate, setValue]);

  return (
    <form onSubmit={handleSubmit(save)}>
      <Stack gap={3} m={3}>
        <Typography level="h3">
          <Trans id="Register event" />
        </Typography>

        {isFoundation && (
          <Alert
            color="primary"
            variant="outlined"
            endDecorator={
              <Button
                variant="outlined"
                size="sm"
                component="a"
                target="_blank"
                href="https://startuptools.org/se/vilken-datum-startades-mitt-bolag/"
              >
                <Trans id="Read more" />
              </Button>
            }
          >
            <Trans id="How do you choose the correct start date? Read this Help article for more information." />
          </Alert>
        )}
        <>
          <>
            {!next && (
              <Alert color="primary" variant="outlined">
                <Trans id="All events from Bolagsverket are processed. You can pre-register share issues or splits in Bolago' share register, and later connect them to the relevant case number from Bolagsverket." />
              </Alert>
            )}
            {next?.kind === GqlTransactionKind.CurrencyChange && (
              <Alert variant="outlined" color="danger">
                <Trans id="Note: this event involves the change of book keeping currency. Our platform only has rudimentary support for this. Please contact us for more information about this issue." />
              </Alert>
            )}
            <Controller
              control={control}
              name="kind"
              defaultValue={next ? 'next' : GqlTransactionKind.Issue}
              render={({ field }) => (
                <FormControl>
                  <FormLabel>
                    <Trans id="Kind" />
                  </FormLabel>

                  <RadioGroup {...field}>
                    {next && (
                      <Radio
                        value="next"
                        label={
                          <Trans
                            id="Next case from Bolagsverket: {name}{filingNumber}"
                            values={{
                              name: `${nameCapitalize(next.name)}`,
                              filingNumber:
                                next.kind !== GqlTransactionKind.Split && next.kind !== GqlTransactionKind.Merge
                                  ? ` - ${next.filingNumber}/${next.filingYear}`
                                  : null,
                            }}
                          />
                        }
                      />
                    )}
                    {!next && (
                      <Radio
                        value={GqlTransactionKind.Issue}
                        disabled={!shareTransactionGroups.length}
                        label={<Trans id="Share Issue" />}
                      />
                    )}
                    {!next && (
                      <Radio
                        value={GqlTransactionKind.Split}
                        disabled={
                          !shareTransactionGroups.length ||
                          Boolean(
                            shareTransactionGroups.filter(
                              stg => stg.kind === GqlTransactionKind.Split && !stg.snrShareCapitalChangeId,
                            ).length,
                          )
                        }
                        label={<Trans id="Split" />}
                      />
                    )}
                    <Radio
                      value={GqlTransactionKind.Transfer}
                      disabled={!shareTransactionGroups.length}
                      label={<Trans id="Share Transfer" />}
                    />
                    <Radio
                      value={GqlTransactionKind.ShareClassConvert}
                      disabled={!shareTransactionGroups.length}
                      label={<Trans id="Share class convert" />}
                    />
                    <Radio
                      value={GqlTransactionKind.BlockPartition}
                      disabled={!shareTransactionGroups.length}
                      label={<Trans id="Notification of Transfer" />}
                    />
                  </RadioGroup>
                </FormControl>
              )}
            />
          </>

          <Controller
            name="date"
            control={control}
            disabled={loading}
            render={({ field }) => (
              <FormControl sx={{ maxWidth: 250 }}>
                <FormLabel>
                  <Trans id="Date" />
                </FormLabel>
                <DateInput {...field} minDate={minDate} maxDate={maxDate} />
              </FormControl>
            )}
          />

          <Suspense
            fallback={
              <Box>
                <Skeleton variant="text" sx={{ maxWidth: 200 }} />
                <Skeleton variant="rectangular" height={40} />
              </Box>
            }
          >
            <TransactionGroupFormSameDaySts
              control={control}
              formState={formState}
              shareTransactionGroups={shareTransactionGroups}
              snrShareCapitalChanges={snrShareCapitalChanges}
            />
          </Suspense>
          {isAddingSplit && (
            <Suspense
              fallback={
                <>
                  <Box>
                    <Skeleton variant="text" sx={{ maxWidth: 200 }} />
                    <Skeleton variant="rectangular" height={40} />
                  </Box>
                  <Box>
                    <Skeleton variant="text" sx={{ maxWidth: 200 }} />
                    <Skeleton variant="rectangular" height={40} />
                  </Box>
                </>
              }
            >
              <TransactionGroupFormSplit
                control={control}
                setValue={setValue}
                currentScc={next}
                stgs={shareTransactionGroups}
              />
            </Suspense>
          )}
        </>

        {error && <ErrorMsg error={error} />}
        <Stack direction="row" gap={1} justifyContent="end" alignItems="center">
          <Button variant="plain" color="neutral" disabled={loading} onClick={close}>
            <Trans id="Cancel" />
          </Button>
          <Button type="submit" loading={loading}>
            {isAddingSplit ? <Trans id="Confirm" /> : <Trans id="Continue" />}
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};
